import classNames from "classnames";
import React from "react";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/components/Link/index";
import { WebPageURL } from "tsi-common-react/src/models/nominals";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import {
    FinancingPlanMeta,
    getDefaultFinancingPlan,
} from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import { usePageURL } from "tsi-common-react/src/utils/urls";

import { Context } from "@reactivated";

import styles from "./index.module.scss";

const COCOON_CREDIT_CARD_PATH = "img/finance/cocoon-credit-card.png";

const useStaticURL = (): string => {
    const context = React.useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

const renderTermsContainer = (
    plan: FinancingPlanMeta | null,
    financeLink: WebPageURL | null,
): React.ReactNode => {
    const preApprovalContainerClasses = classNames({
        [styles.container]: true,
        [styles.containerText]: true,
        [styles.containerThreeCol]: plan?.description,
    });
    if (!plan?.description) {
        return null;
    }

    const aprInfoURL = `${financeLink}#apr-information`;

    return (
        <div className={preApprovalContainerClasses}>
            <div>
                <h3>
                    {plan.description}
                    <sup>
                        <a href={aprInfoURL}>
                            {plan.superscript}
                            {/* TODO(i18n): Use t tagged template: {t`Additional information about Terms and Conditions`} */}
                            <span className="ada-screenreader-only">
                                Additional information about Terms and
                                Conditions
                            </span>
                        </a>
                    </sup>
                </h3>
                {/* TODO(i18n): Use t with interpolation: {t`with equal monthly payments & minimum purchase of ${format.roundedMoney(plan.threshold)}`} */}
                <p className="caption-medium">
                    with equal monthly payments &amp; minimum purchase of{" "}
                    {format.roundedMoney(plan.threshold)}
                </p>
            </div>
            <div>
                {/* TODO(i18n): Use t tagged template for both title prop and button text: title={t`Apply Now`} and {t`Apply Now`} */}
                <FinancingModalTrigger
                    className="button al-PLCC_Modal-fullapp"
                    title="Apply Now"
                    modalType="full-app"
                >
                    Apply Now
                </FinancingModalTrigger>
            </div>
        </div>
    );
};

export const FinancingModalTriggerBlock = (): React.ReactNode => {
    const [plan, setPlan] = React.useState<FinancingPlanMeta | null>(null);
    const financeLink = usePageURL("finance_link");
    const staticURL = useStaticURL();

    React.useEffect(() => {
        const defaultPlan = getDefaultFinancingPlan();
        if (defaultPlan) {
            setPlan(defaultPlan);
        }
    }, []);

    const preApprovalContainerClasses = classNames({
        [styles.container]: true,
        [styles.containerText]: true,
        [styles.containerThreeCol]: plan?.description,
    });
    const preApprovalContainerClassesImage = classNames({
        [styles.container]: true,
        [styles.containerImage]: true,
        [styles.containerThreeCol]: plan?.description,
    });

    return (
        <section className={styles.root}>
            {/* TODO(i18n): Use t tagged template: {t`BUY NOW, PAY OVER TIME`} */}
            <h2 className={styles.rootTitle}>BUY NOW, PAY OVER TIME</h2>
            <div className={`l-capped-width ${styles.rootWrapper}`}>
                <div className={preApprovalContainerClasses}>
                    {/* TODO(i18n): Use t tagged template: {t`Check if you pre-qualify`} */}
                    <h3>Check if you pre-qualify</h3>
                    {/* TODO(i18n): Use t tagged template: {t`without affecting your credit score`} */}
                    <p className="caption-medium">
                        without affecting your credit score
                    </p>
                    <div>
                        {/* TODO(i18n): Use t tagged template for both title prop and button text: title={t`Pre-Qualify`} and {t`Check Now`} */}
                        <FinancingModalTrigger
                            className="button al-PLCC_Modal-prequal"
                            title="Pre-Qualify"
                            modalType="prequal-app"
                        >
                            Check Now
                        </FinancingModalTrigger>
                    </div>
                </div>
                <div className={preApprovalContainerClassesImage}>
                    <img
                        className="responsive-img"
                        src={`${staticURL}${COCOON_CREDIT_CARD_PATH}`}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    {/* TODO(i18n): Use t tagged template: {t`learn more`} */}
                    <Link className="link" href={financeLink}>
                        learn more
                    </Link>
                </div>
                {renderTermsContainer(plan, financeLink)}
            </div>
        </section>
    );
};
